import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  BILLING_DEMO_ENABLED,
  BillingSampleSource,
  noteBillingSample,
  pickSample,
  sampleAccount,
  sampleAddons,
  sampleBudgets,
  sampleBuy,
  sampleCancelPlan,
  sampleCatalogue,
  sampleDetails,
  sampleInvoices,
  sampleKeepPlan,
  sampleLimits,
  samplePlanChange,
  samplePurchases,
  sampleRemoveAddon,
  sampleSaveDetails,
  sampleSaveLimits,
  sampleSetBudget,
  sampleSwitchPlan,
  sampleUsage,
} from './billing-demo-backend';

/**
 * Stands in for the Plans & billing endpoints `backend/api` does not have yet. Rules and data live in
 * {@link ./billing-demo-backend.ts}; this file is the wiring.
 *
 * Two rules, both self-cancelling:
 * 1. It acts only on the billing page (`/organizations/{id}/settings/billing…`); every other page's requests
 *    pass through untouched, even to the same addresses (the Home also reads `/organization/{id}/billing`).
 * 2. The real request always goes out; only a 404 ("no such endpoint") is answered from samples. Status 0,
 *    401, 403 and 5xx reach the page as they are. So the page moves to the real backend endpoint by endpoint.
 */

/** `/organizations/123/settings/billing` and its tabs; never another settings section. */
export function onBillingRoute(routerUrl: string): boolean {
  const path = routerUrl.split(/[?#]/)[0];
  return /^\/organizations\/[^/]+\/settings\/billing(\/|$)/.test(path);
}

/** What a request is for this interceptor; `null` = not ours. Exported for the spec. */
export interface BillingKind {
  source: BillingSampleSource;
  answer: () => unknown;
}

export function classify(req: HttpRequest<unknown>): BillingKind | null {
  let path: string;
  try {
    path = new URL(req.url, 'http://x').pathname;
  } catch {
    path = req.url.split('?')[0];
  }
  const method = req.method;
  const body = (req.body ?? {}) as any;
  let m: RegExpMatchArray | null;

  if (method === 'GET' && /\/billing\/catalogue$/.test(path)) return { source: 'catalogue', answer: sampleCatalogue };

  // Billing details are the Home's organization part (shared address).
  if ((m = path.match(/\/organization\/([^/]+)\/billing$/))) {
    if (method === 'GET') return { source: 'details', answer: sampleDetails };
    if (method === 'PUT') return { source: 'details', answer: () => sampleSaveDetails(body) };
    return null;
  }

  if (!(m = path.match(/\/billing\/([^/]+)\/(.+)$/))) return null;
  const orgId = m[1];
  const rest = m[2];

  if (method === 'GET' && rest === 'account') return { source: 'account', answer: () => sampleAccount(orgId) };
  if (method === 'GET' && rest === 'usage') return { source: 'usage', answer: sampleUsage };
  if (rest === 'credit/purchases') {
    if (method === 'GET') return { source: 'purchases', answer: samplePurchases };
    if (method === 'POST') return { source: 'purchases', answer: () => sampleBuy(Number(body.amount) || 0) };
  }
  if (rest === 'limits') {
    if (method === 'GET') return { source: 'limits', answer: sampleLimits };
    if (method === 'PUT') return { source: 'limits', answer: () => sampleSaveLimits(body) };
  }
  if (method === 'GET' && rest === 'budgets') return { source: 'budgets', answer: sampleBudgets };
  if ((m = rest.match(/^budgets\/([^/]+)$/))) {
    const projectId = decodeURIComponent(m[1]);
    if (method === 'PUT') return { source: 'budgets', answer: () => sampleSetBudget(projectId, body.amount == null ? null : Number(body.amount)) };
    if (method === 'DELETE') return { source: 'budgets', answer: () => sampleSetBudget(projectId, null) };
  }
  if (method === 'GET' && rest === 'plan/change') {
    const to = req.params.get('to') ?? new URL(req.url, 'http://x').searchParams.get('to') ?? '';
    return { source: 'plan', answer: () => samplePlanChange(to) };
  }
  if (method === 'DELETE' && rest === 'plan/change') return { source: 'plan', answer: sampleKeepPlan };
  if (method === 'PUT' && rest === 'plan') return { source: 'plan', answer: () => sampleSwitchPlan(String(body.planId ?? '')) };
  if (rest === 'plan/cancel') {
    if (method === 'POST') return { source: 'plan', answer: () => sampleCancelPlan(true) };
    if (method === 'DELETE') return { source: 'plan', answer: () => sampleCancelPlan(false) };
  }
  if (method === 'GET' && rest === 'addons') return { source: 'addons', answer: sampleAddons };
  if (method === 'DELETE' && (m = rest.match(/^addons\/([^/]+)$/))) {
    const id = decodeURIComponent(m[1]);
    return { source: 'addons', answer: () => sampleRemoveAddon(id) };
  }
  if (method === 'POST' && (m = rest.match(/^addons\/([^/]+)\/keep$/))) {
    const id = decodeURIComponent(m[1]);
    return { source: 'addons', answer: () => sampleRemoveAddon(id, true) };
  }
  if (method === 'GET' && rest === 'invoices') return { source: 'invoices', answer: sampleInvoices };
  // The provider's card page cannot open from samples: an empty address tells the page so.
  if (method === 'POST' && rest === 'card/session') return { source: 'card', answer: () => ({ url: '' }) };
  return null;
}

export const billingDemoBackendInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!BILLING_DEMO_ENABLED) return next(req);
  const kind = classify(req);
  if (!kind) return next(req);

  // inject() only works here, in the injection context.
  const router = inject(Router);
  const url = router.getCurrentNavigation()?.finalUrl?.toString() ?? router.url;
  if (!onBillingRoute(url)) return next(req);
  pickSample(url);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // 404 = no such endpoint yet; anything else is a real failure the page must see.
      if (error.status !== 404) return throwError(() => error);
      noteBillingSample(kind.source);
      return of(new HttpResponse({ status: 200, body: kind.answer(), url: req.url }));
    }),
  );
};
